"use client";

import React, { useState } from "react";
import { FaClipboardCheck, FaSearch } from "react-icons/fa";
import { FiBook, FiPlus } from "react-icons/fi";
import { useQuery, useMutation } from "react-query";
import toast from "react-hot-toast";
import { api } from "../../../../../../../../api/learningPath"; // adjust path if needed
import Loader from "../../../../../../../../components/loader/Loader";

interface ContentItem {
  id: string;
  title: string;
  description: string;
  duration?: string;
  status?: "published" | "draft";
  type: "course" | "assessment";
  questions?: number;
}

interface ContentBucketsProps {
  onAddItem: (item: ContentItem) => void;
  handleModalClose?: () => void;
}

const ContentBuckets: React.FC<ContentBucketsProps> = ({
  onAddItem,
  handleModalClose,
}) => {
  const [activeTab, setActiveTab] = useState<"courses" | "assessments">(
    "courses"
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedItems, setSelectedItems] = useState<ContentItem[]>([]);

  const orgId = JSON.parse(sessionStorage.getItem("user") || "{}")?.org?.id;
  const currentPath = JSON.parse(sessionStorage.getItem("active-path") || "{}");
  console.log(orgId, "orgId");

  // === FETCH COURSES ===
  const {
    data: courses,
    isLoading: isCoursesLoading,
    refetch: refetchCourses,
  } = useQuery(
    ["orgCourses", { searchQuery }],
    () => api.getCourses({ search: searchQuery }),
    {
      select: (data) => data?.data || [],
      refetchOnWindowFocus: false,
    }
  );

  // === FETCH ASSESSMENTS ===
  const { data: assessments, isLoading: isAssessmentsLoading } = useQuery(
    ["assessments", orgId],
    () => api.getExams(orgId),
    {
      select: (data) => data?.data?.data || [],
      refetchOnWindowFocus: false,
    }
  );

  console.log(assessments, "assessments");

  // === MUTATION: ASSIGN TO LEARNING PATH ===
  const assignMutation = useMutation(api.addCourses, {
    onSuccess: (res) => {
      toast.remove();
      toast.success(res?.message || "Added successfully!");
      setSelectedItems([]);
      handleModalClose?.();
    },
    onError: ({ response }) => {
      toast.remove();
      toast.error(response?.data?.message || "Something went wrong!");
    },
  });

  const handleAddToPath = () => {
    if (selectedItems.length < 1)
      return toast.error("Please select items to add to the learning path.");

    toast.loading("Assigning to learning path...");

    const payload = {
      courses: selectedItems.map((item) => ({
        type: item.type,
        courseId: item.type === "course" ? item.id : null,
        assessmentId: item.type === "assessment" ? item.id : null,
      })),
    };

    assignMutation.mutate({
      payload,
      org_id: orgId,
      journey_id: currentPath?.id,
    });
  };

  const toggleSelect = (item: ContentItem) => {
    setSelectedItems((prev) => {
      const exists = prev.find((p) => p.id === item.id);
      return exists ? prev.filter((p) => p.id !== item.id) : [...prev, item];
    });
  };

  const currentItems = activeTab === "courses" ? courses : assessments;
  const filteredItems =
    currentItems?.filter((item: any) => {
      const title =
        activeTab === "courses"
          ? item.name
          : item.pipeline?.title || item.preferred_exam_title;
      return title?.toLowerCase().includes(searchQuery.toLowerCase());
    }) || [];

  return (
    <div className="w-1/3 bg-gray-50 border-r border-gray-200 flex flex-col">
      {/* Tabs */}
      <div className="bg-white border-b border-gray-200">
        <nav className="flex">
          <button
            onClick={() => setActiveTab("courses")}
            className={`flex-1 px-4 py-3 text-sm font-medium flex items-center justify-center ${
              activeTab === "courses"
                ? "text-customOrange border-b-2 border-customOrange bg-blue-50"
                : "text-gray-600 hover:text-gray-900"
            }`}
          >
            <FiBook size={16} className="mr-2" />
            Courses ({courses?.length || 0})
          </button>
          <button
            onClick={() => setActiveTab("assessments")}
            className={`flex-1 px-4 py-3 text-sm font-medium flex items-center justify-center ${
              activeTab === "assessments"
                ? "text-customOrange border-b-2 border-customOrange bg-blue-50"
                : "text-gray-600 hover:text-gray-900"
            }`}
          >
            <FaClipboardCheck size={16} className="mr-2" />
            Assessments ({assessments?.length || 0})
          </button>
        </nav>
      </div>

      {/* Search */}
      <div className="p-4 bg-white border-b border-gray-200">
        <div className="relative mb-3">
          <input
            type="text"
            placeholder={`Search ${activeTab}...`}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <FaSearch size={16} className="absolute left-3 top-3 text-gray-400" />
        </div>
      </div>

      {/* Items List */}
      <div className="flex-1 overflow-y-auto">
        {isCoursesLoading || isAssessmentsLoading ? (
          <div className="flex items-center justify-center h-full">
            <Loader />
          </div>
        ) : (
          <div className="p-4 space-y-3">
            {filteredItems?.map((item: any) => {
              const isSelected = selectedItems?.some((s) => s.id === item.id);
              return (
                <div
                  key={item.id}
                  onClick={() =>
                    toggleSelect({
                      id: item.id,
                      title:
                        activeTab === "courses"
                          ? item.name
                          : item.pipeline?.title || item.preferred_exam_title,
                      description:
                        activeTab === "courses"
                          ? item.description
                          : item.pipeline?.description || "",
                      type: activeTab === "courses" ? "course" : "assessment",
                    })
                  }
                  className={`bg-white border rounded-lg p-4 cursor-pointer transition ${
                    isSelected
                      ? "border-darkBlue shadow-md"
                      : "border-gray-200 hover:border-blue-300"
                  }`}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h3 className="font-medium text-gray-900">
                        {activeTab === "courses"
                          ? item.name
                          : item.pipeline?.title || item.preferred_exam_title}
                      </h3>
                      <p
                        className="text-sm text-gray-600 mt-1"
                        dangerouslySetInnerHTML={{
                          __html:
                            activeTab === "courses"
                              ? item.description
                              : item.pipeline?.description || "",
                        }}
                      ></p>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onAddItem(item);
                      }}
                      className="text-darkBlue hover:bg-blue-50 p-2 rounded"
                    >
                      <FiPlus size={16} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Footer Button */}
      <div className="p-4 border-t border-gray-200 bg-white">
        <button
          onClick={handleAddToPath}
          disabled={selectedItems.length < 1}
          className={`w-full py-2 rounded-md font-medium ${
            selectedItems.length > 0
              ? "bg-darkBlue text-white hover:bg-darkBlue"
              : "bg-gray-200 text-gray-500 cursor-not-allowed"
          }`}
        >
          Add to Path {selectedItems.length > 0 && `(${selectedItems.length})`}
        </button>
      </div>
    </div>
  );
};

export default ContentBuckets;
